import { useRef } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Grid } from '@material-ui/core'
import {
  ListBase,
  Datagrid,
  useRecordContext,
  RichTextField,
} from 'react-admin'
import moment from 'moment'
import Slide from '@mui/material/Slide'
import { styled } from '@mui/material/styles'
import { keyframes } from '@mui/system'
import config from './config'

const shadow = keyframes`
	0% {
		box-shadow: 0 5px 15px 0px rgba(0,0,0,0.6);
		transform: translatey(0px);
	}
	50% {
		box-shadow: 0 25px 15px 0px rgba(0,0,0,0.2);
		transform: translatey(5px);
	}
	100% {
		box-shadow: 0 5px 15px 0px rgba(0,0,0,0.6);
		transform: translatey(0px);
	}

`

const useStyles = makeStyles({
  mainGrid: {
    backgroundColor: '#CBE5F3',
    padding: '30px',
  },
  icon: {
    width: '128px',
  },
  gridMargin: {
    marginBottom: '2rem',
  },
  alignCenter: {
    textAlign: 'center',
  },
  alignRight: {
    textAlign: 'right',
  },
  title: {
    fontWeight: 'bold',
    fontSize: '48px',
    marginBottom: '64px',
  },
  body: {
    fontSize: '24px',
  },
  remarkedLine: {
    fontWeight: 'bold',
    fontSize: '32px',
  },
  bubbleLeft: {
    position: 'relative',
    padding: '9px 48px',
    marginLeft: 'auto',
    borderRadius: '15px',
    textAlign: 'center',
    color: '#000',
    '&::before': {
      content: '""',
      width: '0px',
      height: '0px',
      position: 'absolute',
      borderRight: '12px solid transparent',
      borderBottom: '20px solid transparent',
      left: '32px',
      bottom: '-24px',
    },
  },
  bubbleRight: {
    position: 'relative',
    margin: 'auto',
    padding: '9px 48px',
    borderRadius: '15px',
    textAlign: 'center',
    color: '#000',
    '&::before': {
      content: '""',
      width: '0px',
      height: '0px',
      position: 'absolute',
      borderLeft: '12px solid transparent',
      borderBottom: '20px solid transparent',
      right: '32px',
      bottom: '-24px',
    },
  },
  bubbleGreen: {
    background: '#a4e5ce',
    borderBottom: '4px solid #237154',
    '&::before': {
      borderRight: '24px solid #a4e5ce',
      borderTop: '12px solid #a4e5ce',
    },
  },
  bubbleLast: {},
  bubbleMain: {
    background: '#E9DCA8',
    borderBottom: '4px solid #AA860D',
    '&::before': {
      borderRight: '24px solid #E9DCA8',
      borderTop: '12px solid #E9DCA8',
    },
  },
  bubbleSecondary: {
    background: '#a4e5ce',
    borderBottom: '4px solid #237154',
    '&::before': {
      borderLeft: '24px solid #a4e5ce',
      borderTop: '12px solid #a4e5ce',
    },
  },
  bubbleRed: {
    background: '#F7C3C6',
    borderBottom: '4px solid #8F212A',
    '&::before': {
      borderLeft: '24px solid #F7C3C6',
      borderTop: '12px solid #F7C3C6',
    },
  },
  bubbleYellow: {
    background: '#E9DCA8',
    borderBottom: '4px solid #AA860D',
    '&::before': {
      borderRight: '24px solid #E9DCA8',
      borderTop: '12px solid #E9DCA8',
    },
  },
  rightColWith: {
    width: '128px',
  },
  released_at_style: {
    fontSize: '8px',
    color: 'grey',
    position: 'absolute',
    right: '1rem',
  },
})

const LastRelease = styled('div')({
  animation: `${shadow} 6s ease-in-out infinite`,
  background: '#CBE5F3',
  border: '4px solid #143A5F',
  textAlign: 'center',
  margin: '20px auto',
  borderRadius: '15px',
  padding: '10px',
})

const ReleasesRender = ({ rowIdx }) => {
  const { id, released_at, ...record } = useRecordContext()
  const classes = useStyles()
  const released_at_formatted = moment(released_at).format('D/M/Y H:ss')
  const current = rowIdx.current
  rowIdx.current = current + 1

  if (0 === current)
    return (
      <Grid item xs={12}>
        <LastRelease>
          <span className={classes.released_at_style}>
            {released_at_formatted}
          </span>
          <p>
            <RichTextField source="description" record={record} />
          </p>
        </LastRelease>
      </Grid>
    )

  if (current % 2)
    return (
      <Slide direction="right" in={true} mountOnEnter>
        <Grid
          item
          xs={12}
          className={`${classes.alignRight} ${classes.gridMargin}`}
        >
          <div
            className={`${classes.alignRight} ${classes.bubbleRight} ${classes.bubbleMain}`}
          >
            <span className={classes.released_at_style}>
              {released_at_formatted}
            </span>
            <p>
              <RichTextField source="description" record={record} />
            </p>
          </div>
        </Grid>
      </Slide>
    )
  return (
    <Slide direction="left" in={true} mountOnEnter>
      <Grid
        item
        xs={12}
        className={`${classes.alignRight} ${classes.gridMargin}`}
      >
        <div
          className={`${classes.alignRight} ${classes.bubbleLeft} ${classes.bubbleSecondary}`}
        >
          <span className={classes.released_at_style}>
            {released_at_formatted}
          </span>
          <p>
            <RichTextField source="description" record={record} />
          </p>
        </div>
      </Grid>
    </Slide>
  )
}

export const LandingPage = () => {
  const publicUrl = config.PUBLIC_URL
  const classes = useStyles()

  const rowIdx = useRef(0)
  return (
    <Grid container p={4} spacing={2} className={classes.mainGrid}>
      <Grid item xs={7}>
        <ListBase resource="releases">
          <Datagrid bulkActionButtons={false}>
            <ReleasesRender label="Releases" rowIdx={rowIdx} />
          </Datagrid>
        </ListBase>
      </Grid>
      <Grid container item xs={5} style={{ alignItems: 'end' }}>
        <Grid
          item
          xs={12}
          className={`${classes.alignCenter}  ${classes.gridMargin}`}
        >
          <div
            className={`${classes.rightColWith} ${classes.bubbleRight} ${classes.bubbleGreen}`}
          >
            <img
              className={`${classes.icon} `}
              src={`${publicUrl}/img/icons/mail.svg`}
            />
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          className={`${classes.alignRight} ${classes.gridMargin}`}
        >
          <div
            className={`${classes.rightColWith} ${classes.alignRight} ${classes.bubbleLeft} ${classes.bubbleRed}`}
          >
            <img
              className={`${classes.icon} `}
              src={`${publicUrl}/img/icons/siren.svg`}
            />
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          className={`${classes.alignCenter}  ${classes.gridMargin}`}
        >
          <div
            className={`${classes.rightColWith} ${classes.icon} ${classes.bubbleRight} ${classes.bubbleYellow}`}
          >
            <img src={`${publicUrl}/img/icons/buildings.svg`} />
          </div>
        </Grid>
      </Grid>
    </Grid>
  )
}
